/**
 * Formatting and the status tag.
 *
 * The three-way reading is the prototype's (`design/Clinic Stock v2.dc.html:3110`)
 * and is a product decision: a clinic reads these as a traffic light — fine,
 * watch it, act now. Since 27 October 2026 the tags are small and outlined: a
 * pale fill, a thin line of the same hue, 6px corners. Only the states that
 * are already too late, out of stock and expired, stay solid, so they still
 * stand out from "reorder" and "use soon" at a glance.
 * Money arrives from the API as integer pence and is only ever turned into
 * pounds here, at the last possible moment.
 */
import type { CSSProperties, ReactNode } from 'react';
import type { ExpiryStatusKey, StockStatusKey } from '../lib/api.js';
import { c, sans } from '../theme.js';

const OK = { fg: c.okText, bg: c.okBg, line: c.okLine, dot: '#4F7A62' };
const WATCH = { fg: c.amberText, bg: c.amberBg, line: c.amberLine, dot: '#B98A2E' };
const ACT = { fg: c.danger, bg: c.dangerBg, line: c.dangerLine, dot: c.danger };
/** Already too late: out of stock, expired. The only solid tags. */
const GONE = { fg: c.white, bg: c.danger, line: c.danger, dot: c.danger };

export const palette: Record<string, { fg: string; bg: string; line: string; dot: string }> = {
  ok: OK,
  low: WATCH,
  reorder: ACT,
  out: GONE,
  fresh: OK,
  soon: WATCH,
  urgent: ACT,
  expired: GONE,
  na: { fg: c.muted2, bg: c.white, line: c.border, dot: '#9A9D93' },
};

/**
 * Pence to pounds. The prototype writes whole pounds
 * (design/Clinic Stock v2.dc.html:3128); pence only show when there are any, so
 * a tidy £120 does not become a noisy £120.00 across a KPI row.
 */
export function money(pence: number): string {
  const pounds = pence / 100;
  return `£${pounds.toLocaleString('en-GB', {
    minimumFractionDigits: pence % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * For figures that are ESTIMATES — pace against a straight line, a projected
 * month-end, an average. Rounded to the pound, as the prototype does
 * (design/Clinic Stock v2.dc.html:5095): "£2,096.77 behind" claims a precision
 * a straight-line projection does not have. Never for money that was actually
 * charged or counted; that is `money`.
 */
export function moneyRounded(pence: number): string {
  return money(Math.round(pence / 100) * 100);
}

/**
 * Pounds typed by a person → integer pence.
 *
 * Money is integer pence everywhere (CLAUDE.md rule 1) and this is the doorway
 * it comes in through, so it is the one place a rounding mistake becomes a
 * wrong price on a real product. `Math.round` rather than truncation because
 * `19.99 * 100` is `1998.9999999999998` in binary floating point, and `| 0`
 * would quietly charge a penny less forever.
 *
 * Returns null for anything that is not a non-negative number, so callers
 * distinguish "they cleared the field" from "they typed nonsense".
 */
export function poundsToPence(input: string): number | null {
  const trimmed = input.trim().replace(/^£/, '');
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

/** Integer pence → a plain editable string in pounds. No symbol, no commas. */
export function penceToPounds(pence: number | null): string {
  if (pence === null) return '';
  return (pence / 100).toFixed(2);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "5 Sep 2026" — the prototype's `fmtDate`. */
export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '—';
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

export function formatDateTime(iso: string): string {
  const dt = new Date(iso);
  return `${formatDate(iso.slice(0, 10))}, ${dt.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

/**
 * When a movement HAPPENED, which is `occurred_on` — not `created_at`, which is
 * when the row was written.
 *
 * The two are the same thing on the normal path: log a treatment now and the
 * business date is today. They come apart whenever stock is entered after the
 * fact — a delivery logged on Monday for Friday, a stock count reconciled a week
 * late, or a seeded demo account. There the created date is an implementation
 * detail and showing it makes the ledger read as though everything happened at
 * once, which is exactly what a movement history is for denying.
 *
 * The clock time is kept only when the two agree. On a backdated row there is no
 * meaningful time of day to show, and inventing one from the insert timestamp
 * would be a small lie on an append-only record.
 */
export function formatMovementWhen(occurredOn: string, createdAt: string): string {
  const occurred = occurredOn.slice(0, 10);
  if (occurred === createdAt.slice(0, 10)) return formatDateTime(createdAt);
  return formatDate(occurred);
}

/**
 * "1 unit" / "7 units", but "7 ml" and "7 box" left exactly as typed.
 *
 * `unit_label` is per-product and written by the clinic, so it can be "vial",
 * "box", "ml" or anything else. Appending an "s" to all of them produces "boxs"
 * and "mls", which is worse than the "7 unit" it would be fixing. Only our own
 * default is pluralised — the case that is both overwhelmingly common and the
 * only one whose plural we actually know.
 */
export function unitCount(qty: number, label: string | null): string {
  if (!label) return String(qty);
  if (label === 'unit') return `${qty} ${qty === 1 ? 'unit' : 'units'}`;
  return `${qty} ${label}`;
}

/**
 * A movement `reason` as a person would read it: `stock_count` → "Stock count".
 *
 * Movement reasons come from several vocabularies — write-off reasons, variance
 * reasons, and system ones like `received` and `void` — so there is no single
 * label map to look them up in. Humanising the raw value covers all of them and
 * cannot go stale when a new reason is added, which an exhaustive map would.
 */
export function humaniseReason(reason: string): string {
  const spaced = reason.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function Pill({
  tone,
  children,
}: {
  tone: StockStatusKey | ExpiryStatusKey | 'neutral';
  children: ReactNode;
}) {
  const p = palette[tone] ?? palette.na!;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 500,
        lineHeight: 1.5,
        whiteSpace: 'nowrap',
        color: p.fg,
        background: p.bg,
        border: `1px solid ${p.line}`,
      }}
    >
      {children}
    </span>
  );
}

export function Dot({ tone }: { tone: string }) {
  return (
    <span
      style={{
        width: 9,
        height: 9,
        borderRadius: '50%',
        flexShrink: 0,
        background: palette[tone]?.dot ?? c.green,
        display: 'inline-block',
      }}
    />
  );
}

export const cardStyle: CSSProperties = {
  background: c.card,
  // Cards sit on the stone without an outline. Transparent rather than absent
  // so a caller can still set `borderColor` for an error state.
  border: '1px solid transparent',
  borderRadius: 18,
  padding: '22px 24px',
};

export const smallButton: CSSProperties = {
  background: '#EEF2EF',
  border: '1px solid transparent',
  borderRadius: 980,
  padding: '7px 14px',
  fontSize: 13.5,
  fontWeight: 500,
  fontFamily: sans,
  color: c.ink,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
